import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addMinutes, differenceInWeeks, format, parse, parseJSON, setHours, setMinutes } from "date-fns";
import { Box, Button, CheckBox, FormField, Grid, Heading, List, ResponsiveContext, Select, Stack, Tab, Tabs, Text, TextInput } from "grommet";
import { flatten, groupBy, mapValues, set, sum } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import AutoSizeTextArea from "../components/AutoSizeTextArea";
import { FormatDateTime } from "../components/DateTimePicker";
import DaySelector, { DaysArray } from "../components/DaySelector";
import { NavigationButton } from "../components/NavigationButton";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import { TeamRow } from "../teams/TeamsManagement";
import { getTeams, TeamViewModel } from "../teams/TeamsStore";
import SeasonDetails from "./SeasonDetails";
import { SeasonMatchLine } from "./SeasonMatchLine";
import {
    addParticipant,
    AddSeasonParticipantRequest,
    createMatchSchedule,
    revertSchedule,
    DeleteSeasonScheduleRequest,
    getSeason,
    implementMatchSchedule,
    ImplementMatchScheduleRequest,
    implementSchedule,
    ImplementSeasonScheduleRequest,
    removeParticipant,
    RemoveSeasonParticipantRequest,
    SeasonMatchViewModel,
    SeasonParticipantViewModel,
    SeasonRound,
    SeasonScheduleSettings,
    SeasonViewModel,
    updateSchedule,
    updateSeason,
    UpdateSeasonScheduleRequest,
    deleteSchedule,
    RevertSeasonScheduleRequest,
} from "./SeasonsStore";

import * as chroma from "chroma-js";
import { getStats, StatViewModel } from "../stats/StatsStore";

import { addMilliseconds } from "date-fns/esm";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { GetOrganizations } from "../organizations/IdentityAPI";
import SeasonSchedule, { SeasonMatchScheduleByDay, SeasonRoundsByDay } from "./SeasonSchedule";
import SeasonSchedulePlanner from "./SeasonSchedulePlanner";
import SeasonTable from "./SeasonTable";
import { Organization } from "../organizations/OrganizationStore";
import _ from "lodash";

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const calendar = <FontAwesomeIcon icon={["fad", "calendar"]}></FontAwesomeIcon>;
const chain = <FontAwesomeIcon icon={["fad", "chain"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;

interface ParticipantMatches {
    participant: SeasonParticipantViewModel;
    matches: SeasonMatchViewModel[];
}

const SeasonProfile = () => {
    const user = useUser();
    const [organizations, setOrganizations] = React.useState([] as Organization[]);
    React.useEffect(() => {
        GetOrganizations().then((response) => {
            setOrganizations(response);
        });
    }, []);

    let { seasonID } = useParams<{ seasonID: string }>();

    let { pathname } = useLocation();
    let navigate = useNavigate();

    const [season, setSeason] = React.useState(new SeasonViewModel());
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        if (season.id) {
            if (pathname == `/seasons/${seasonID}`) {
                if (season.locked) {
                    navigate(`/seasons/${seasonID}/schedule/view/calendar`, { replace: true });
                } else {
                    navigate(`/seasons/${seasonID}/teams`, { replace: true });
                }
            }
        }
    }, [pathname, season.id]);

    const [rules, setRules] = React.useState(season.rules || "");
    const [rulesAsMarkdown, setRulesAsMarkdown] = React.useState(season.rulesAsMarkdown || false);

    let [error, setError] = React.useState("");
    const [statTypes, setStatTypes] = React.useState([] as StatViewModel[]);

    React.useEffect(() => {
        getStats().then((response) => {
            setStatTypes(response.result);
        });
    }, []);

    React.useEffect(() => {
        loadSeason();
    }, [statTypes]);

    const size = React.useContext(ResponsiveContext);

    let [participantMatches, setParticipantsMatches] = React.useState([] as ParticipantMatches[]);

    let [participants, setParticipants] = React.useState([] as SeasonParticipantViewModel[]);
    const [selectedRound, setSelectedRound] = React.useState(1);
    const [selectedRoundMatches, setSelectedRoundMatches] = React.useState([] as SeasonMatchViewModel[]);

    const loadSeason = () => {
        // setIsLoading(true);
        getSeason(seasonID).then((season) => {
            setSeason(season);
            setIsLoading(false);
            setRules(season.rules);
            setRulesAsMarkdown(season.rulesAsMarkdown);
            setParticipants(season.participants || []);
            setSeasonDuration(differenceInWeeks(parseJSON(season.endDate), parseJSON(season.startDate)));
            try {
                setSelectedRoundMatches(season.schedule.rounds[selectedRound - 1].matches);
            } catch {}
            setParticipantsMatches(
                season.participants?.map((p) => {
                    let matches = season.schedule.matches.filter((m) => m.participantTeamIDs.some((pm) => pm == p.teamID));
                    return {
                        participant: p,
                        matches,
                    } as ParticipantMatches;
                }) || []
            );

            // setPagedMatches(season.schedule.matches.slice(0, pageSize));
            setupSchedule(season.schedule.rounds, season);
        });
    };

    const [isUpdating, setIsUpdating] = React.useState(false);
    const [seasonDuration, setSeasonDuration] = React.useState(0);

    const onChange = (updatedSeason) => {
        setSeason({ ...season, ...updatedSeason });
        setSeasonDuration(differenceInWeeks(parseJSON(updatedSeason.endDate), parseJSON(updatedSeason.startDate)));
    };

    let update = () => {
        setIsUpdating(true);
        updateSeason({ ...season, rules, rulesAsMarkdown } as SeasonViewModel).then(
            (result) => {
                loadSeason();
                setIsUpdating(false);
            },
            (error) => {
                setError(error);
                setIsUpdating(false);
            }
        );
    };

    const [teams, setTeams] = React.useState([] as TeamViewModel[]);

    React.useEffect(() => {
        getTeams("").then((teams) => {
            setTeams(teams);
            setTeamSearchResult(teams);
        });
    }, []);

    let [teamSearchResult, setTeamSearchResult] = React.useState([] as Array<TeamViewModel>);

    const search = (filter) => {
        setTeamSearchResult(
            teams.filter((r) => {
                return r.name.toLowerCase().includes(filter.toLowerCase());
            })
        );
    };

    let [teamFilter, setTeamFilter] = React.useState("");
    const [role, setRole] = React.useState("");

    React.useEffect(() => {
        search(teamFilter);
    }, [teamFilter]);

    const [showAddParticipantModal, setShowAddParticipantModal] = React.useState(false);
    const [showRemoveParticipantModal, setShowRemoveParticipantModal] = React.useState(false);

    const [participantToRemove, setParticipantToRemove] = React.useState({} as TeamViewModel);

    const [participantCreateError, setParticipantCreateError] = React.useState("");
    const [participantRemoveError, setParticipantRemoveError] = React.useState("");

    const addParticipantToSeason = (team: TeamViewModel) => {
        setParticipantCreateError("");
        setIsLoading(true);
        addParticipant({ seasonID: season.id, teamID: team.id } as AddSeasonParticipantRequest).then((response) => {
            if (response.success) {
                loadSeason();
            } else {
                setParticipantCreateError(response.errors.join("\n"));
                setIsLoading(false);
            }
        });
    };

    const startRemoveParticipant = (participant: TeamViewModel) => {
        setParticipantToRemove(participant);
        setShowRemoveParticipantModal(true);
    };

    const removeParticipantFromSeason = () => {
        setParticipantRemoveError("");
        setIsLoading(true);
        removeParticipant({ seasonID: season.id, teamID: participantToRemove.id } as RemoveSeasonParticipantRequest).then((response) => {
            if (response.success) {
                loadSeason();
                setShowRemoveParticipantModal(false);
            } else {
                setParticipantRemoveError(response.errors.join("\n"));
                setIsLoading(false);
            }
        });
    };

    const ScheduleGridProps =
        size == "small"
            ? { rows: ["auto"], columns: new Array(2).fill(size) }
            : {
                  rows: ["auto"],
                  columns: new Array(3).fill("1/3"),
                  //   columns: new Array(tournament.bracket.rounds.length).fill(tournament.bracket.rounds.length >= 4 ? "18%" : tournament.bracket.rounds.length > 2 ? "25%" : "45%"),
              };

    // const [pageSize, setPageSize] = React.useState(5);

    // const [pagedMatches, setPagedMatches] = React.useState((season.schedule.matches || []).slice(0, pageSize));

    // const [page, setPage] = React.useState(1);

    // const doPage = ({ startIndex, endIndex, page }) => {
    //     setPagedMatches(season.schedule.matches.slice(startIndex, endIndex));
    //     setPage(page);
    // };

    const [selectedTeam, setSelectedTeam] = React.useState(-1);
    // const [selectedRoundTeams, setSelectedRoundTeams] = React.useState([] as any);

    // const selectRound = (i) => () => {
    //     setSelectedRound(i);
    //     setSelectedRoundMatches(season.schedule.rounds[i - 1].matches);

    //     let teams = flatten(season.schedule.rounds[i - 1].matches.map((m) => m.match?.participants.map((p) => p.team))).filter((t) => t);

    //     setSelectedRoundTeams(entries(groupBy(teams, (t) => t.name)).map((g) => ({ team: g[1][0], count: g[1].length })));
    // };

    const startSeason = () => {
        setIsLoading(true);
        implementSchedule({ seasonID: season.id } as ImplementSeasonScheduleRequest).then((response) => {
            if (response.success) {
                setShowImplementMatchSchedule(false);
                loadSeason();
            }
            setIsLoading(false);
        });
    };

    const hasWritePermission = usePermission("sport:seasons.write");
    const [scheduleConcurrentMatches, setScheduleConcurrentMatches] = React.useState(1);
    const [scheduleTimeOnly, setScheduleTimeOnly] = React.useState(false);
    const [scheduleMatchesPerDay, setScheduleMatchesPerDay] = React.useState(1);
    const [scheduleMatchesGap, setScheduleMatchesGap] = React.useState(0);
    const [scheduleMatchesDuration, setScheduleMatchesDuration] = React.useState(15);
    const [scheduleDays, setScheduleDays] = React.useState(DaysArray.slice(1, 6));
    const [scheduleStartTime, setScheduleStartTime] = React.useState("13:00");

    React.useEffect(() => {}, [scheduleTimeOnly, scheduleMatchesPerDay, scheduleMatchesGap, scheduleMatchesDuration, scheduleDays, scheduleStartTime]);

    const [matchScheduleByDay, setMatchScheduleByDay] = React.useState({} as SeasonMatchScheduleByDay);
    const [roundsByDay, setRoundsByDay] = React.useState({} as SeasonRoundsByDay);

    const [showImplementMatchSchedule, setShowImplementMatchSchedule] = React.useState(false);

    const createSchedule = () => {
        let timeUtc = scheduleStartTime;

        if (/^([0-9]|[01][0-9]|2[0-3]):([0-5][0-9]$|[0-9])$/.test(timeUtc)) {
            let [hours, minutes] = timeUtc.split(":");

            let newDate = setHours(new Date(), parseInt(hours));
            newDate = setMinutes(newDate, parseInt(minutes));

            newDate = addMilliseconds(newDate, newDate.getTimezoneOffset() * 60 * 1000);

            timeUtc = format(newDate, "HH:mm");
        }

        createMatchSchedule(season, {
            concurrentMatches: scheduleConcurrentMatches,
            scheduleTimeOnly: scheduleTimeOnly,
            availableDays: scheduleDays.map((d) => DaysArray.indexOf(d)),
            matchesPerDay: scheduleMatchesPerDay,
            matchesGapMinutes: scheduleMatchesGap,
            matchDurationMinutes: scheduleMatchesDuration,
            startTime: timeUtc,
        } as SeasonScheduleSettings).then((response) => {
            setupSchedule(response.rounds, season);
            navigate(`/seasons/${season.id}/schedule/view/calendar`);
            setShowImplementMatchSchedule(true);
        });
    };

    const setupSchedule = (rounds: SeasonRound[], season: SeasonViewModel) => {
        let resolvedRounds = rounds.map((r) => ({ ...r, matches: r.matches.map((m) => ({ ...m, match: { ...season.matches.filter((sm) => sm.id == m.matchID)[0], startDate: m.matchStartDate } })) }));

        // orderBy(flatten(resolvedRounds[0].matches), (m) => parseJSON(m.match.startDate), ["asc"]);

        // setMatchSchedule(orderBy(flatten(resolvedRounds[0].matches), (m) => parseJSON(m.match.startDate), ["asc"]));

        // setMatchSchedule(resolvedRounds.map((r) => ({ ...r, days: groupBy(r.matches, (m) => FormatDateTime(parseJSON(m.matchStartDate), "yyyy/MM/dd")) })));
        let matchScheduleByDayAndTime = _(flatten(resolvedRounds.map((r) => r.matches)))
            .groupBy((m) => FormatDateTime(parseJSON(m.matchStartDate), "yyyy/MM/dd"))
            .mapValues((matches) =>
                _(matches)
                    .groupBy((m) => FormatDateTime(parseJSON(m.matchStartDate), "HH:mm"))
                    .value()
            )
            .value();

        setMatchScheduleByDay(matchScheduleByDayAndTime);

        // setMatchScheduleByDay(groupBy(flatten(resolvedRounds.map((r) => r.matches)), (m) => FormatDateTime(parseJSON(m.matchStartDate), "yyyy/MM/dd")));
        setRoundsByDay(
            mapValues(
                groupBy(flatten(rounds.map((r) => r.matches.map((m) => ({ date: m.matchStartDate, roundNumber: r.roundNumber })))), (m) => FormatDateTime(parseJSON(m.date), "yyyy/MM/dd")),
                (m) => m[0].roundNumber
            )
        );
    };

    const doImplementMatchSchedule = async () => {
        setIsUpdating(true);
        let timeUtc = scheduleStartTime;

        if (/^([0-9]|[01][0-9]|2[0-3]):([0-5][0-9]$|[0-9])$/.test(timeUtc)) {
            let [hours, minutes] = timeUtc.split(":");

            let newDate = setHours(new Date(), parseInt(hours));
            newDate = setMinutes(newDate, parseInt(minutes));

            newDate = addMilliseconds(newDate, newDate.getTimezoneOffset() * 60 * 1000);

            timeUtc = format(newDate, "HH:mm");
        }

        let response = await implementMatchSchedule({
            seasonID: season.id,
            settings: {
                concurrentMatches: scheduleConcurrentMatches,
                scheduleTimeOnly: scheduleTimeOnly,
                availableDays: scheduleDays.map((d) => DaysArray.indexOf(d)),
                matchesPerDay: scheduleMatchesPerDay,
                matchesGapMinutes: scheduleMatchesGap,
                matchDurationMinutes: scheduleMatchesDuration,
                startTime: timeUtc,
            } as SeasonScheduleSettings,
        } as ImplementMatchScheduleRequest);

        if (response.success) {
            loadSeason();
            setShowImplementMatchSchedule(false);
        }
        setIsUpdating(false);
    };

    const [showArchiveModal, setShowArchiveModal] = React.useState(false);

    const startArchive = () => {
        setShowArchiveModal(true);
    };

    const archive = () => {
        updateSeason({ ...season, archived: !season.archived } as SeasonViewModel).then(() => {
            loadSeason();
            setShowArchiveModal(false);
        });
    };

    const [showRevertModal, setShowRevertModal] = React.useState(false);
    const [showDeleteScheduleModal, setShowDeleteScheduleModal] = React.useState(false);
    const [revertError, setRevertError] = React.useState("");

    const startRevertSeason = () => {
        setShowRevertModal(true);
    };

    const revertSeason = () => {
        setIsUpdating(true);
        revertSchedule({ seasonID: season.id } as RevertSeasonScheduleRequest).then((res) => {
            setRevertError(res.errors?.join(", "));
            loadSeason();
            setShowRevertModal(false);
            setIsUpdating(false);
        });
    };

    const startDeleteSeasonSchedule = () => {
        setShowDeleteScheduleModal(true);
    };

    const deleteSeasonSchedule = () => {
        setIsUpdating(true);
        deleteSchedule({ seasonID: season.id } as DeleteSeasonScheduleRequest).then((res) => {
            setRevertError(res.errors?.join(", "));
            loadSeason();
            setShowRevertModal(false);
            setIsUpdating(false);
        });
    };

    const addScheduleRound = () => {
        let newRound = {
            roundNumber: season.schedule.rounds.length + 1,
            matches: [],
        } as SeasonRound;

        let newRounds = [...season.schedule.rounds, newRound];

        updateSchedule({ id: season.id, schedule: { ...season.schedule, rounds: newRounds } } as UpdateSeasonScheduleRequest).then(() => {
            loadSeason();
        });
    };

    const addScheduleMatch = (roundNumber: number, participantOne, participantTwo) => {
        setNewMatchError("");

        if (!participantOne || !participantTwo) {
            setNewMatchError("Please select two participants");
            return;
        }

        if (participantOne == participantTwo) {
            setNewMatchError("Cannot add a match with the same participant");
            return;
        }

        let matchCount = sum(season.schedule.rounds.map((r) => r.matches.length));
        let rounds = season.schedule.rounds;

        for (var i = 0; i < rounds.length; i++) {
            if (rounds[i].roundNumber == roundNumber) {
                rounds[i].matches.push({
                    participantTeamIDs: [participantOne, participantTwo],
                    seasonMatchID: matchCount + 1,
                } as SeasonMatchViewModel);
            }
        }

        updateSchedule({ id: season.id, schedule: { ...season.schedule, rounds: rounds } } as UpdateSeasonScheduleRequest).then(() => {
            loadSeason();
            setShowAddMatchParticipantModal(false);
        });
    };

    const [showAddMatchParticipantModal, setShowAddMatchParticipantModal] = React.useState(false);

    const [selectedRoundNumber, setSelectedRoundNumber] = React.useState(0);
    const [newMatchParticipantOne, setNewMatchParticipantOne] = React.useState("");
    const [newMatchParticipantTwo, setNewMatchParticipantTwo] = React.useState("");
    const [newMatchError, setNewMatchError] = React.useState("");

    const startAddRoundMatch = (roundNumber: number) => {
        setSelectedRoundNumber(roundNumber);
        setShowAddMatchParticipantModal(true);
        setNewMatchError("");
        setNewMatchParticipantOne("");
        setNewMatchParticipantTwo("");
    };

    const [matchIDToRemove, setMatchIDToRemove] = React.useState(-1);

    const startRemoveMatch = (matchID) => {
        setMatchIDToRemove(matchID);
        setShowRemoveMatchModal(true);
    };

    const [showRemoveMatchModal, setShowRemoveMatchModal] = React.useState(false);

    const removeMatch = () => {
        if (!season.locked) {
            let rounds = season.schedule.rounds;

            for (var i = 0; i < rounds.length; i++) {
                rounds[i].matches = rounds[i].matches.filter((m) => m.seasonMatchID != matchIDToRemove);
            }

            let newMatchID = 1;

            for (var i = 0; i < rounds.length; i++) {
                for (var j = 0; j < rounds[i].matches.length; j++) {
                    rounds[i].matches[j].seasonMatchID = newMatchID++;
                }
            }

            updateSchedule({ id: season.id, schedule: { ...season.schedule, rounds: rounds } } as UpdateSeasonScheduleRequest).then(() => {
                loadSeason();
            });
        }
    };

    const [roundIDToRemove, setRoundIDToRemove] = React.useState(-1);

    const startRemoveRound = (roundID) => {
        setRoundIDToRemove(roundID);
        setShowRemoveRoundModal(true);
    };

    const [showRemoveRoundModal, setShowRemoveRoundModal] = React.useState(false);

    const removeRound = async () => {
        if (!season.locked) {
            let rounds = season.schedule.rounds;

            for (var i = 0; i < rounds.length; i++) {
                rounds = rounds.filter((r) => r.roundNumber != roundIDToRemove);
            }

            let newMatchID = 1;

            for (var i = 0; i < rounds.length; i++) {
                for (var j = 0; j < rounds[i].matches.length; j++) {
                    rounds[i].matches[j].seasonMatchID = newMatchID++;
                }
                rounds[i].roundNumber = i + 1;
            }

            await updateSchedule({ id: season.id, schedule: { ...season.schedule, rounds: rounds } } as UpdateSeasonScheduleRequest);

            loadSeason();
            setShowRemoveRoundModal(false);
        }
    };

    const [showAllMatches, setShowAllMatches] = React.useState(false);

    const handleDrop = (droppedItem) => {
        let destIndex = droppedItem.destination.index;
        let sourceIndex = droppedItem.source.index;

        // Ignore drop outside droppable container
        if (!droppedItem.destination) return;
        // var updatedList = [...currentQuestions];
        // var x = currentQuestions[destIndex].sortKey;
        // var y = currentQuestions[sourceIndex].sortKey;

        // // Remove dragged item
        // const [reorderedItem] = updatedList.splice(sourceIndex, 1);

        // // Add dropped item
        // updatedList.splice(destIndex, 0, reorderedItem);

        // // Update State
        // setCurrentQuestions(updatedList);

        // const updatePromises = [] as Promise<any>[];

        // if (sourceIndex < destIndex) {
        //     for (let i = 0; i <= destIndex - sourceIndex; i++) {
        //         updatePromises.push(UpdateQuestion({ ...updatedList[destIndex - i], sortKey: x - i }));
        //     }
        // } else {
        //     for (let i = 0; i <= sourceIndex - destIndex; i++) {
        //         updatePromises.push(UpdateQuestion({ ...updatedList[sourceIndex - i], sortKey: y - i }));
        //     }
        // }

        let sourceRoundNumber = +droppedItem.source.droppableId;
        let sourceMatchIndex = droppedItem.source.index;
        let destinationRoundNumber = +droppedItem.destination.droppableId;
        let destinationMatchIndex = droppedItem.destination.index;

        let rounds = season.schedule.rounds;

        let sourceRound = rounds.filter((r) => r.roundNumber == sourceRoundNumber)[0];
        let destinationRound = rounds.filter((r) => r.roundNumber == destinationRoundNumber)[0];

        let sourceMatch = sourceRound.matches[sourceMatchIndex];
        //remove from source
        sourceRound.matches = sourceRound.matches.filter((m) => m.seasonMatchID != sourceMatch.seasonMatchID);

        if (droppedItem.destination.droppableId == "delete") {
        } else {
            destinationRound.matches.splice(destinationMatchIndex, 0, sourceMatch);
        }

        let newMatchID = 1;

        for (var i = 0; i < rounds.length; i++) {
            for (var j = 0; j < rounds[i].matches.length; j++) {
                rounds[i].matches[j].seasonMatchID = newMatchID++;
            }
            rounds[i].roundNumber = i + 1;
        }

        updateSchedule({ id: season.id, schedule: { ...season.schedule, rounds: rounds } } as UpdateSeasonScheduleRequest).then(() => {
            loadSeason();
        });
    };

    const [plannerMatchesInput, setPlannerMatchesInput] = React.useState("");
    const [plannerRound, setPlannerRound] = React.useState({} as SeasonRound);
    const [plannerMatches, setPlannerMatches] = React.useState([] as SeasonMatchViewModel[]);

    React.useEffect(() => {
        let splitRegex = /(\,|\t)/;

        let matchCount = sum(season.schedule.rounds.map((r) => r.matches.length));

        let matches = plannerMatchesInput
            .split("\n")
            .filter((t) => t)
            .map((match) => {
                let teams = match
                    .split(splitRegex)
                    .filter((t) => splitRegex.test(t) == false)
                    .map((p) => p.trim().toLowerCase())
                    .filter((t, i) => i < 2);

                let date = new Date(match.split(splitRegex)[4]);
                return {
                    teams: season.participants
                        .map((p) => p.team)
                        .filter((t) => teams.includes(t.name.toLowerCase()))
                        .map((t) => t.id),
                    date,
                };
            })
            .filter(({ teams }) => teams.length == 2)
            .map(({ teams, date }) => {
                return {
                    seasonMatchID: ++matchCount,
                    participantTeamIDs: teams,
                    sortKey: matchCount - 1,
                    matchID: "",
                    matchStartDate: !!+date ? date.toISOString() : "0000-01-01T00:00Z",
                };
            });

        setPlannerMatches(matches);
    }, [plannerMatchesInput]);

    const addPlannerMatchesToRound = async () => {
        let matchCount = sum(season.schedule.rounds.map((r) => r.matches.length));
        let rounds = season.schedule.rounds;

        for (var i = 0; i < rounds.length; i++) {
            if (rounds[i].roundNumber == plannerRound.roundNumber) {
                rounds[i].matches = rounds[i].matches.concat(plannerMatches);
            }
        }

        await updateSchedule({ id: season.id, schedule: { ...season.schedule, rounds: rounds } } as UpdateSeasonScheduleRequest);
        loadSeason();
    };

    return (
        <Box>
            <SkeletonWrapper loading={isLoading}>
                <Heading size="small">
                    <Box direction="row" align="center" gap="small" wrap>
                        {season.league?.name} - {season.name}
                    </Box>
                </Heading>

                <Box direction="row" align="center" justify="center" gap="medium" wrap>
                    {season.locked && <NavigationButton href={`/seasons/${season.id}/table`} text={"Table"}></NavigationButton>}
                    {season.locked && <NavigationButton href={`/seasons/${season.id}/schedule/view/calendar`} text={"Schedule"}></NavigationButton>}
                    <NavigationButton href={`/seasons/${season.id}/matches/list`} alt={`/seasons/${season.id}/matches`} text={"Matches"}></NavigationButton>
                    <NavigationButton href={`/seasons/${season.id}/teams`} text={"Teams"}></NavigationButton>
                    <NavigationButton href={`/seasons/${season.id}/details`} text={"Details"}></NavigationButton>
                    <NavigationButton href={`/seasons/${season.id}/rules`} text={"Rules"}></NavigationButton>
                    {season.league?.isPublic && <NavigationButton href={`/seasons/${season.id}/public`} text={"Public Page"}></NavigationButton>}
                    {season.leagueID && <NavigationButton href={`/leagues/${season.leagueID}`} text={season?.league?.name}></NavigationButton>}
                    {error && <NavigationButton href={`/seasons/${season.id}/error`} text={"Errors"}></NavigationButton>}
                </Box>
                <br />
                <Routes>
                    <Route
                        path="table"
                        element={
                            <>
                                <SeasonTable season={season} statTypes={statTypes}></SeasonTable>
                            </>
                        }
                    />
                    <Route
                        path="schedule/*"
                        element={
                            <>
                                <Box direction="row" align="center" justify="center" gap="medium" wrap>
                                    <NavigationButton href={`/seasons/${season.id}/schedule/view/calendar`} text={"Details"}></NavigationButton>
                                    <NavigationButton href={`/seasons/${season.id}/schedule/settings`} text={"Settings"}></NavigationButton>
                                    {showImplementMatchSchedule && <Button label="Implement Schedule" icon={isUpdating ? <FontAwesomeIcon icon={["fad", "spinner"]} spin={true}></FontAwesomeIcon> : undefined} disabled={isUpdating} onClick={doImplementMatchSchedule} />}
                                </Box>
                                <Routes>
                                    <Route
                                        path="view/*"
                                        element={
                                            <>
                                                <SeasonSchedule
                                                    disabled={showImplementMatchSchedule}
                                                    season={season}
                                                    matchScheduleByDay={matchScheduleByDay}
                                                    roundsByDay={roundsByDay}
                                                    onChange={() => {
                                                        loadSeason();
                                                    }}
                                                ></SeasonSchedule>
                                            </>
                                        }
                                    />
                                    <Route
                                        path="settings"
                                        element={
                                            <>
                                                <Box>
                                                    <Grid columns={["auto", "auto"]} gap="small">
                                                        <Box>
                                                            <Box direction="row" gap="small">
                                                                <FormField label="Time slots per day">
                                                                    <TextInput type="number" placeholder="1" value={scheduleMatchesPerDay} onChange={(e) => setScheduleMatchesPerDay(+e.target.value)} />
                                                                </FormField>
                                                                <FormField label="Concurrent Matches">
                                                                    <TextInput type="number" placeholder="1" value={scheduleConcurrentMatches} onChange={(e) => setScheduleConcurrentMatches(+e.target.value)} />
                                                                </FormField>
                                                            </Box>
                                                            <Box direction="row" gap="small">
                                                                <FormField label="Match duration" info="minutes">
                                                                    <TextInput type="number" placeholder="15" value={scheduleMatchesDuration} onChange={(e) => setScheduleMatchesDuration(+e.target.value)} />
                                                                </FormField>
                                                                <FormField label="Gap between matches" info="minutes">
                                                                    <TextInput type="number" placeholder="5" value={scheduleMatchesGap} onChange={(e) => setScheduleMatchesGap(+e.target.value)} />
                                                                </FormField>
                                                            </Box>
                                                            <Box direction="row" gap="small">
                                                                {!scheduleTimeOnly && (
                                                                    <Box>
                                                                        <Text margin={{ left: "small" }}>Available Days</Text>
                                                                        <DaySelector
                                                                            days={scheduleDays}
                                                                            onChange={(days) => {
                                                                                setScheduleDays(days);
                                                                            }}
                                                                        ></DaySelector>
                                                                    </Box>
                                                                )}
                                                                <FormField label="Start time">
                                                                    <TextInput type="time" value={scheduleStartTime} onChange={(e) => setScheduleStartTime(e.target.value)}></TextInput>
                                                                </FormField>
                                                            </Box>
                                                        </Box>
                                                        <Box>
                                                            <Text>Week Template</Text>
                                                            <Box direction="row" fill="horizontal" gap="small">
                                                                <Box fill="horizontal" gap="small" background={"background-back"} pad="small">
                                                                    <Text>Day</Text>
                                                                    {new Array(scheduleMatchesPerDay).fill("").map((m, i) => (
                                                                        <Box key={i} fill="horizontal">
                                                                            {format(addMinutes(parse(scheduleStartTime, "HH:mm", new Date()), (scheduleMatchesDuration + scheduleMatchesGap) * i), "HH:mm")}
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                                {scheduleDays.map((day) => (
                                                                    <Box key={day} fill="horizontal" gap="small" background={"background-back"} pad="small">
                                                                        <Text>{day}</Text>
                                                                        {new Array(scheduleMatchesPerDay).fill("").map((m, i) => (
                                                                            <Box key={i} fill="horizontal">
                                                                                {scheduleConcurrentMatches} Matches
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    </Grid>

                                                    <FormField label="Schedule Type">
                                                        <CheckBox label="Schedule Times only (use existing match dates)" checked={scheduleTimeOnly} onChange={(e) => setScheduleTimeOnly(e.target.checked)} />
                                                    </FormField>

                                                    <Text>
                                                        {scheduleMatchesPerDay} time slots per day, {scheduleConcurrentMatches} concurrent matches, {scheduleDays.length} days a week for {seasonDuration} weeks
                                                    </Text>
                                                    <Text>{scheduleMatchesPerDay * scheduleConcurrentMatches * scheduleDays.length * seasonDuration} match slots available</Text>
                                                    <Text color={scheduleMatchesPerDay * scheduleConcurrentMatches * scheduleDays.length * seasonDuration < (season.scheduleType == "RoundRobin" ? season.schedule.requiredMatches : season.schedule.matches.length) ? "status-critical" : "text"}>
                                                        <Text>
                                                            {season.scheduleType == "RoundRobin" && <Text>{season.schedule.requiredMatches} </Text>}
                                                            {season.scheduleType == "Custom" && <Text>{season.schedule.matches.length} </Text>}
                                                        </Text>
                                                        <Text>
                                                            Matches are required for {seasonDuration} weeks from {FormatDateTime(parseJSON(season.startDate), "dd MMMM")} to {FormatDateTime(parseJSON(season.endDate), "dd MMMM")}
                                                        </Text>
                                                    </Text>
                                                    <Text>
                                                        Matches start at {scheduleStartTime} on <Text color="brand">{scheduleDays.join(", ")}</Text> and end at {format(addMinutes(parse(scheduleStartTime, "HH:mm", new Date()), (scheduleMatchesDuration + scheduleMatchesGap) * scheduleMatchesPerDay), "HH:mm")}
                                                    </Text>

                                                    <Button label="Create Match Schedule" disabled={scheduleMatchesPerDay * scheduleConcurrentMatches * scheduleDays.length * seasonDuration < (season.scheduleType == "RoundRobin" ? season.schedule.requiredMatches : season.schedule.matches.length)} onClick={createSchedule}></Button>
                                                </Box>
                                            </>
                                        }
                                    />
                                </Routes>
                            </>
                        }
                    />
                    <Route
                        path="matches/*"
                        element={
                            <Box margin={"small"}>
                                {season.scheduleType == "RoundRobin" && <Text>{season.schedule.requiredMatches} Matches</Text>}
                                {season.scheduleType == "Custom" && <Text>{season.schedule.matches.length} Matches</Text>}

                                <Box direction="row" align="center" justify="center" gap="medium" wrap>
                                    <NavigationButton href={`/seasons/${season.id}/matches/list`} text={"List"}></NavigationButton>
                                    <NavigationButton href={`/seasons/${season.id}/matches/teams`} text={"Teams"}></NavigationButton>
                                    {season.scheduleType == "Custom" && <NavigationButton href={`/seasons/${season.id}/matches/planner`} text={"Planner"}></NavigationButton>}
                                </Box>
                                <br />
                                <Routes>
                                    <Route
                                        path="list"
                                        element={
                                            <>
                                                {hasWritePermission && !season.locked && (
                                                    <Box pad={{ vertical: "small" }} direction="row">
                                                        <Button label="Start Season" onClick={startSeason}></Button>
                                                    </Box>
                                                )}
                                                {season.scheduleType == "RoundRobin" && seasonDuration > 0 && (
                                                    <Grid {...ScheduleGridProps} gap="small" margin={{ right: "medium" }}>
                                                        {season.schedule.matches.map((m, i) => (
                                                            <Box key={"m" + i}>
                                                                <SeasonMatchLine seasonMatch={m} teams={season.participants.map((p) => p.team)}></SeasonMatchLine>
                                                            </Box>
                                                        ))}
                                                    </Grid>
                                                )}
                                                {season.scheduleType == "Custom" && (
                                                    <DragDropContext onDragEnd={handleDrop}>
                                                        <>
                                                            {!season.locked && season.schedule.rounds.length > 0 && season.schedule.rounds.some((r) => r.matches.length > 0) && (
                                                                <Droppable droppableId={"delete"}>
                                                                    {(provided, snapshot) => (
                                                                        <Box justify="center" align="center" pad="medium" {...provided.droppableProps} ref={provided.innerRef} border={{ style: "solid", color: "status-critical", size: "2px" }}>
                                                                            <Text>Drop to remove</Text>
                                                                            {provided.placeholder}
                                                                        </Box>
                                                                    )}
                                                                </Droppable>
                                                            )}
                                                            {season.schedule.rounds.map((round) => (
                                                                <Box key={"r" + round.roundNumber}>
                                                                    <Droppable droppableId={round.roundNumber.toString()}>
                                                                        {(provided, snapshot) => (
                                                                            <Box {...provided.droppableProps} ref={provided.innerRef}>
                                                                                <Box direction="row" pad={{ vertical: "small" }} justify="between">
                                                                                    <Text size="large">Round {round.roundNumber}</Text>
                                                                                    <Box>
                                                                                        {!season.locked && season.scheduleType == "Custom" && (
                                                                                            <Box direction="row" gap="small">
                                                                                                <Button label="Add Match" onClick={() => startAddRoundMatch(round.roundNumber)}></Button>
                                                                                                <Button color={"status-critical"} label="Remove Round" onClick={() => startRemoveRound(round.roundNumber)}></Button>
                                                                                            </Box>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>

                                                                                <Grid {...ScheduleGridProps} gap="small" margin={{ right: "medium" }}>
                                                                                    {season.locked &&
                                                                                        round.matches.map((m, i) => (
                                                                                            <Box key={"m" + i}>
                                                                                                <SeasonMatchLine seasonMatch={m} teams={season.participants.map((p) => p.team)}></SeasonMatchLine>
                                                                                            </Box>
                                                                                        ))}
                                                                                    {!season.locked &&
                                                                                        round.matches.map((m, i) => (
                                                                                            <Draggable key={"m" + i} draggableId={m.seasonMatchID.toString()} index={i}>
                                                                                                {(provided, snapshot) => (
                                                                                                    <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} hoverIndicator={{ background: "status-critical" }} border={{ size: "small", color: snapshot.isDragging ? "brand" : "transparent" }}>
                                                                                                        <Stack anchor="bottom" fill>
                                                                                                            <SeasonMatchLine seasonMatch={m} teams={season.participants.map((p) => p.team)}></SeasonMatchLine>
                                                                                                            <Text size="small">{FormatDateTime(parseJSON(m.matchStartDate), "dd MMMM HH:mm")}</Text>
                                                                                                        </Stack>
                                                                                                    </Box>
                                                                                                )}
                                                                                            </Draggable>
                                                                                        ))}
                                                                                </Grid>
                                                                                {provided.placeholder}
                                                                            </Box>
                                                                        )}
                                                                    </Droppable>
                                                                </Box>
                                                            ))}
                                                        </>
                                                    </DragDropContext>
                                                )}
                                                {!season.locked && season.scheduleType == "Custom" && (
                                                    <Box direction="row" gap="small" justify="end" pad={{ vertical: "small" }}>
                                                        <Button label="Add Round" onClick={addScheduleRound}></Button>
                                                    </Box>
                                                )}
                                            </>
                                        }
                                    ></Route>
                                    <Route
                                        path="teams"
                                        element={
                                            <>
                                                <Button label={showAllMatches ? "Hide All" : "Show All"} onClick={() => setShowAllMatches(!showAllMatches)}></Button>
                                                {participantMatches.map((participantMatch, i) => (
                                                    <Box key={"pm" + i} pad="small">
                                                        <TeamRow team={participantMatch.participant.team} key={"t" + i}>
                                                            <Box direction="row" align="center" gap="small">
                                                                <Text>{participantMatch.matches.length} Matches</Text>
                                                                {!showAllMatches && <Button label={selectedTeam == i ? "Hide Matches" : "Show Matches"} onClick={() => setSelectedTeam(selectedTeam == i ? -1 : i)}></Button>}
                                                            </Box>
                                                        </TeamRow>
                                                        {(selectedTeam == i || showAllMatches) && (
                                                            <Box gap="small" direction="row" wrap justify="center">
                                                                {participantMatch.matches.map((match, j) => (
                                                                    <Box key={"m" + j} pad={{ top: "small" }} width="small">
                                                                        <SeasonMatchLine seasonMatch={match} teams={season.participants.map((p) => p.team)}></SeasonMatchLine>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                            </>
                                        }
                                    ></Route>
                                    <Route
                                        path="planner"
                                        element={
                                            <>
                                                <SeasonSchedulePlanner value={plannerMatchesInput} onChange={(value) => setPlannerMatchesInput(value)} teams={season.participants.map((p) => p.team)}></SeasonSchedulePlanner>
                                                <Box gap="small">
                                                    {plannerMatches.map((match, i) => (
                                                        <Stack anchor="bottom" fill key={"s" + match.seasonMatchID}>
                                                            <SeasonMatchLine seasonMatch={match} teams={season.participants.map((p) => p.team)}></SeasonMatchLine>
                                                            <Text size="small">{FormatDateTime(parseJSON(match.matchStartDate), "dd MMMM HH:mm")}</Text>
                                                        </Stack>
                                                    ))}
                                                </Box>
                                                <Box direction="row" gap="small" align="center">
                                                    <Select placeholder="Select a round" value={plannerRound} onChange={({ value }) => setPlannerRound(value)} options={season.schedule.rounds} labelKey={(item) => "Round " + item.roundNumber} valueKey={"roundNumber"}></Select>
                                                    <Button size="small" label="Add matches to round" onClick={addPlannerMatchesToRound} disabled={!plannerRound.roundNumber || plannerMatches.length == 0}></Button>
                                                </Box>
                                            </>
                                        }
                                    ></Route>
                                </Routes>
                            </Box>
                        }
                    />
                    <Route
                        path="Teams"
                        element={
                            <>
                                <Box pad={"small"} gap="small">
                                    {participants.map((participant, index) => (
                                        <TeamRow team={participant.team} key={index}>
                                            <Box direction="row" gap="small">
                                                {!season.locked && (
                                                    <Box direction="row" gap="small">
                                                        <Button label="Remove" icon={xmark} onClick={() => startRemoveParticipant(participant.team)} color="status-critical"></Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        </TeamRow>
                                    ))}
                                </Box>
                                {!season.locked && <Button icon={plus} label="Add Participant" onClick={() => setShowAddParticipantModal(true)}></Button>}
                            </>
                        }
                    />
                    <Route
                        path="details"
                        element={
                            <>
                                <SeasonDetails season={season} isBusy={isUpdating} onChange={onChange}></SeasonDetails>
                                <br />

                                {hasWritePermission && (
                                    <Box gap="small" direction="row">
                                        {season.archived && <Button onClick={() => startArchive()} label="Unarchive" color="status-ok"></Button>}
                                        {!season.archived && <Button onClick={() => startArchive()} label="Archive" color="status-warning"></Button>}

                                        {season.locked && <Button onClick={startRevertSeason} icon={isUpdating ? <FontAwesomeIcon icon={["fad", "spinner"]} spin={true}></FontAwesomeIcon> : undefined} disabled={isUpdating} color="status-error" label="Revert Season"></Button>}
                                        <Button onClick={startDeleteSeasonSchedule} icon={isUpdating ? <FontAwesomeIcon icon={["fad", "spinner"]} spin={true}></FontAwesomeIcon> : undefined} disabled={isUpdating} color="status-error" label="Delete Schedule"></Button>

                                        <Button primary onClick={update} disabled={isUpdating} label="Update"></Button>
                                    </Box>
                                )}
                            </>
                        }
                    />
                    <Route
                        path="rules"
                        element={
                            <>
                                <Box fill={true}>
                                    <FormField label="Support Markdown">
                                        <CheckBox checked={rulesAsMarkdown} label="Markdown" onChange={(e) => setRulesAsMarkdown(e.target.checked)} disabled={isUpdating} />
                                    </FormField>
                                    <FormField label="Rules">
                                        <AutoSizeTextArea value={rules || ""} onChange={(e) => setRules(e.target.value)} disabled={isUpdating} />
                                    </FormField>
                                </Box>
                            </>
                        }
                    />
                    <Route
                        path="public"
                        element={
                            <Box gap="small">
                                <Button href={"/public/seasons/" + season.id + "/table"} icon={idBadge} label="View Table"></Button>
                                <Button href={"/public/seasons/" + season.id + "/schedule/calendar"} icon={calendar} label="View Schedule"></Button>
                            </Box>
                        }
                    />
                    <Route
                        path="error"
                        element={
                            <>
                                <pre>{JSON.stringify(error, null, 2)}</pre>
                            </>
                        }
                    />
                </Routes>
            </SkeletonWrapper>
            <StyledModal isOpen={showAddParticipantModal} setIsOpen={setShowAddParticipantModal}>
                <Box width={"medium"}>
                    <FormField label="Search">
                        <TextInput size="xs" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} />
                    </FormField>
                    <List
                        pad="xs"
                        data={teamSearchResult}
                        itemKey="id"
                        primaryKey={(team: TeamViewModel) => (
                            <Box direction="row" justify="between" align="center" width={{ min: "315px" }}>
                                <Text size="small" key={team.id}>
                                    <Avatar name={team.name} src={team.logoUri} round size="25px"></Avatar> {team.name}
                                </Text>
                                {user.hasRole("Admin") && <Text size="xsmall">{organizations.filter((o) => o.id == team.organizationID)[0]?.name}</Text>}
                            </Box>
                        )}
                        action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => addParticipantToSeason(item)} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                    ></List>
                    <Text>
                        <pre>{participantCreateError}</pre>
                    </Text>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveParticipantModal} setIsOpen={setShowRemoveParticipantModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this participant?</Text>
                    <Text>
                        <pre>{participantRemoveError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveParticipantModal(false)} label="Cancel"></Button>
                        <Button primary onClick={() => removeParticipantFromSeason()} label="Remove" color="status-critical"></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showArchiveModal} setIsOpen={setShowArchiveModal}>
                <Box width={"medium"}>
                    {season.archived && <Text>Are you sure you want to unarchive this season?</Text>}
                    {!season.archived && <Text>Are you sure you want to archive this season?</Text>}
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowArchiveModal(false)} label="Cancel"></Button>
                        {season.archived && <Button primary onClick={() => archive()} label="Unarchive" color="status-ok"></Button>}
                        {!season.archived && <Button primary onClick={() => archive()} label="Archive" color="status-warning"></Button>}
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRevertModal} setIsOpen={setShowRevertModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to revert this season?</Text>
                    <Text>All matches, outcomes and stats will be lost, schedule dates and times will be kept.</Text>
                    <Text>
                        <pre>{revertError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRevertModal(false)} label="Cancel"></Button>
                        <Button primary onClick={() => revertSeason()} label="Revert" color="status-critical"></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showDeleteScheduleModal} setIsOpen={setShowDeleteScheduleModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to delete the schedule?</Text>
                    <Text>All matches, outcomes and stats will be lost, and the schedule will be cleared.</Text>
                    <Text>
                        <pre>{revertError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRevertModal(false)} label="Cancel"></Button>
                        <Button primary onClick={() => deleteSeasonSchedule()} label="Delete" color="status-critical"></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showAddMatchParticipantModal} setIsOpen={setShowAddMatchParticipantModal}>
                <Box width={"medium"}>
                    <Text>Select First Participant</Text>
                    {!newMatchParticipantOne && (
                        <List
                            pad="xs"
                            data={season.participants.map((p) => p.team)}
                            itemKey="id"
                            primaryKey={(team: TeamViewModel) => (
                                <Text size="small" key={team.id}>
                                    <Avatar name={team.name} src={team.logoUri} round size="25px"></Avatar> {team.name}
                                </Text>
                            )}
                            action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => setNewMatchParticipantOne(item.id)} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                        ></List>
                    )}
                    {newMatchParticipantOne && (
                        <TeamRow team={season.participants.filter((p) => p.teamID == newMatchParticipantOne)[0].team}>
                            <Button icon={<FontAwesomeIcon icon={["fad", "x"]}></FontAwesomeIcon>} onClick={() => setNewMatchParticipantOne("")}></Button>
                        </TeamRow>
                    )}
                    <Text>Select Second Participant</Text>
                    {!newMatchParticipantTwo && (
                        <List
                            pad="xs"
                            data={season.participants.map((p) => p.team)}
                            itemKey="id"
                            primaryKey={(team: TeamViewModel) => (
                                <Text size="small" key={team.id}>
                                    <Avatar name={team.name} src={team.logoUri} round size="25px"></Avatar> {team.name}
                                </Text>
                            )}
                            action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => setNewMatchParticipantTwo(item.id)} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                        ></List>
                    )}
                    {newMatchParticipantTwo && (
                        <TeamRow team={season.participants.filter((p) => p.teamID == newMatchParticipantTwo)[0].team}>
                            <Button icon={<FontAwesomeIcon icon={["fad", "x"]}></FontAwesomeIcon>} onClick={() => setNewMatchParticipantTwo("")}></Button>
                        </TeamRow>
                    )}
                    <Text>
                        <pre>{newMatchError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowAddMatchParticipantModal(false)} label="Cancel"></Button>
                        <Button primary onClick={() => addScheduleMatch(selectedRoundNumber, newMatchParticipantOne, newMatchParticipantTwo)} label="Confirm"></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveMatchModal} setIsOpen={setShowRemoveMatchModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this match?</Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveMatchModal(false)} label="Cancel"></Button>
                        <Button primary onClick={() => removeMatch()} label="Remove" color="status-critical"></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveRoundModal} setIsOpen={setShowRemoveRoundModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this round?</Text>
                    <Text>This will remove all matches in round {roundIDToRemove}</Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveRoundModal(false)} label="Cancel"></Button>
                        <Button primary onClick={() => removeRound()} label="Remove" color="status-critical"></Button>
                    </Box>
                </Box>
            </StyledModal>
        </Box>
    );
};

export default SeasonProfile;
